/**
 * The shape a filter builder row travels in.
 *
 * These types are the contract between the presentational builder (this package), the URL codec
 * that persists an applied set (`@smartsash/data-screen`) and the per-page mapping that turns a
 * set into an endpoint's query string (each app). They live here because the component is the
 * only piece all three have to agree with.
 */

/**
 * The operators the builder offers. `eq` is `Equals`, `contains` is a substring match, `between`
 * bounds a field — the two ends live in `values[0]` and `values[1]`, either of which may be empty
 * for an open-ended range — `lte` is a single upper bound ("on or before"), carried in `values[0]`
 * alone, and `gt` / `lt` are strict single bounds on an ordered field.
 */
export const FILTER_OPERATORS = ['eq', 'contains', 'between', 'lte', 'gt', 'lt'] as const;

/**
 * Derived from the tuple above so the list exists once. The URL codec that validates a persisted
 * set has to accept exactly these six: a schema listing fewer silently drops rows the builder can
 * produce, which is how date filters used to vanish on navigation.
 */
export type FilterOperator = (typeof FILTER_OPERATORS)[number];

/** One row of the filter builder: a field, how to compare it, and what to compare it against. */
export interface FilterCondition {
  field: string;
  operator: FilterOperator;
  /**
   * One entry for a text comparison; one per checked option for an enumeration; exactly two
   * positional entries for `between`, where an empty string means that end is unbounded.
   */
  values: string[];
}
